<template>
    <div>
        <div class="content">
            <h1>{{ lang('My Account') }}</h1>
        </div>

        <b-field label="Name" expanded>
            <b-input v-model="data.name" :class="{'is-danger': formErrors.name}"></b-input>
        </b-field>

        <b-field label="Email" expanded>
            <b-input v-model="data.email" :class="{'is-danger': formErrors.email}"></b-input>
        </b-field>

        <div class="field">
            <b-checkbox v-model="data.event_notifications" :true-value="1" :false-value="0">
                {{ lang('Email me when new opening times are added.') }}
            </b-checkbox>
        </div>

        <div class="field" v-if="data.is_admin">
            <b-checkbox v-model="data.new_registration_notification" :true-value="1" :false-value="0">
                {{ lang('Email me when someone registers on the website.') }}
            </b-checkbox>
        </div>

        <div class="field">
            <b-collapse :open.sync="showChangePasswordForm">
                <a role="button" slot="trigger">
                    <span class="icon">
                        <i class="fa fa-lg" :class="[showChangePasswordForm ? 'fa-angle-down' : 'fa-angle-right']"></i>
                    </span> {{ lang('Change Password') }}
                </a>
                <div class="notification">
                    <div class="content">
                        <b-field :label="lang('Current Password')" expanded>
                            <b-input v-model="current_password" :class="{'is-danger': formErrors.current_password}" type="password"></b-input>
                        </b-field>

                        <b-field :label="lang('New Password')" expanded>
                            <b-input v-model="password" :class="{'is-danger': formErrors.password}" type="password"></b-input>
                        </b-field>

                        <b-field :label="lang('Confirm New Password')" expanded>
                            <b-input v-model="password_confirmation" :class="{'is-danger': formErrors.password_confirmation}" type="password"></b-input>
                        </b-field>
                    </div>
                </div>
            </b-collapse>
        </div>

        <div class="field">
            <p class="control">
                <button class="button is-primary" @click="save()">{{ lang('Save') }}</button>
            </p>
        </div>

        <p v-show="Object.keys(formErrors).length" v-for="(errors, field) in formErrors" :key="field" class="help is-danger">
            <template v-for="error in errors">
                {{ error }}
            </template>
        </p>
        <br><br>

        <div class="content">
            <h1>{{ lang('My Event Hours') }}</h1>

            <p>{{ lang('Total hours by event type.') }}</p>
        </div>

        <b-table :data="statsData" :loading="statsLoading" :default-sort-direction="defaultSortOrder" :default-sort="[sortField, sortOrder]">
            <template slot-scope="props">
                <b-table-column field="year" :label="lang('Year')" sortable>
                    {{ props.row.year }}
                </b-table-column>

                <b-table-column v-for="(hours, type) in props.row.event_hours" :key="type" :field="type + '_hours'" :label="type" numeric :custom-sort="sort(type)" sortable>
                    {{ hours }}
                </b-table-column>

                <b-table-column :label="lang('Timesheet')" :centered="true">
                    <b-dropdown position="is-bottom-left" aria-role="menu">
                        <button class="button is-primary is-small" slot="trigger">
                            <b-icon icon="file-pdf-o"></b-icon>
                            <b-icon icon="caret-down"></b-icon>
                        </button>

                        <b-dropdown-item v-for="month in monthsOfYear(props.row.year)" :key="month.number" has-link aria-role="menuitem">
                            <a @click.prevent="invoiceNumberPrompt(props.row.year, month.name, '/users/timesheet?year='+props.row.year+'&month='+month.number)">
                                {{ lang(month.name) }}
                            </a>
                        </b-dropdown-item>
                    </b-dropdown>
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
export default {
    name: "Settings",
    data() {
        return {
            data: {},
            current_password: "",
            password: "",
            password_confirmation: "",
            formErrors: {},
            showChangePasswordForm: false,
            loading: false,
            statsLoading: false,
            statsData: [],
            sortField: "year",
            sortOrder: "desc",
            defaultSortOrder: "desc",
        };
    },
    methods: {
        /*
         * Load async data
         */
        loadAsyncData() {
            let self = this;
            this.loading = true;

            axios
                .get(`/users/settings`)
                .then(function(response) {
                    self.data = response.data;
                    self.loading = false;
                })
                .catch(function(error) {
                    self.data = {};
                    self.loading = false;
                    if (error.response.status == 401) {
                        // unauthorized
                        self.logout();
                    } else {
                        self.alertCustomError(error.response.data.message);
                    }
                });

            this.statsLoading = true;

            axios
                .get(`/users/stats`)
                .then(function(response) {
                    self.statsData = [];
                    response.data.forEach(item => self.statsData.push(item));
                    self.statsLoading = false;
                })
                .catch(function(error) {
                    self.statsData = [];
                    self.statsLoading = false;
                    if (error.response.status == 401) {
                        // unauthorized
                        self.logout();
                    } else {
                        self.alertCustomError(error.response.data.message);
                    }
                });
        },
        /*
         * Type style in relation to the value
         */
        type(value) {
            const number = parseFloat(value);
            if (number < 6) {
                return "is-danger";
            } else if (number >= 6 && number < 8) {
                return "is-warning";
            } else if (number >= 8) {
                return "is-success";
            }
        },
        alertCustomError(message) {
            this.$buefy.dialog.alert({
                title: "Error",
                message: message,
                type: "is-danger",
                hasIcon: true,
                icon: "times-circle"
            });
        },
        logout() {
            window.location.href = "/logout";
        },
        save() {
            var self = this;

            // include password fields in request
            self.data.current_password = self.current_password;
            self.data.password = self.password;
            self.data.password_confirmation = self.password_confirmation;

            axios
                .patch(`/users/settings`, self.data)
                .then(function(response) {
                    self.formErrors = {};
                    self.$buefy.toast.open({
                        message: "Gespeichert! Alles ist gut.",
                        type: "is-success",
                        position: "is-top-right"
                    });
                    self.showChangePasswordForm = false;
                })
                .catch(function(error) {
                    if (
                        error.response.status == 422 &&
                        error.response.data.errors
                    ) {
                        self.formErrors = error.response.data.errors;
                        self.showEventForm = true;
                    } else if (error.response.status == 401) {
                        // unauthorized
                        self.logout();
                    } else {
                        self.alertCustomError(error.response.data.message);
                    }
                });
        },
        sort(type) {
            return function(a, b, isAsc) {
                a = JSON.parse(JSON.stringify(a.event_hours))[type];
                b = JSON.parse(JSON.stringify(b.event_hours))[type];
                return isAsc ? a - b : b - a;
            };
        },
        monthsOfYear(year) {
            let months = [];
            let baseDate = new Date(Date.UTC(year, 0, 1));
            let now = new Date();
            for(let i = 1; i <= 12; i++)
            {
                if (baseDate > now) {
                    break;
                }
                months.push({'number': i, 'name': baseDate.toLocaleDateString('en', { month: 'long' })});
                baseDate.setMonth(baseDate.getMonth() + 1);
            }
            return months;
        },
        invoiceNumberPrompt(year, month, url) {
            var self = this;
            this.$buefy.dialog.prompt({
                title: self.lang('Timesheet') + ' ' + self.lang(month) + ', ' + year,
                message: self.lang('Enter an Invoice Number'),
                inputAttrs: {
                    placeholder: self.lang('e.g.') + ' ' + this.$auth.user.name.toLowerCase().replace(/ /g, "-") + '-001',
                    maxlength: 40,
                    required: false
                },
                trapFocus: true,
                confirmText: self.lang('Create PDF'),
                cancelText: self.lang('Cancel'),
                onConfirm: (value) => {
                    window.open(url + '&invoice_number=' + value, '_blank');
                }
            });
        }
    },
    filters: {
        /**
         * Filter to truncate string, accepts a length parameter
         */
        truncate(value, length) {
            return value.length > length
                ? value.substr(0, length) + "..."
                : value;
        }
    },
    mounted() {
        this.loadAsyncData();
    }
};
</script>
